import { addProtocol, removeProtocol } from 'maplibre-gl'
import { signRequest } from '@/utilities/api'
import nearmapUsageTracker from '@/utilities/nearmapUsageTracker'

const PROTOCOL_NAME = 'nearmap-tracked'
let protocolRegistered = false

/**
 * MapLibre 6.11.2 protocol handler for Nearmap tile requests with billing tracking
 * Uses Promise-based API: (params, abortController) => Promise<{data: ArrayBuffer}>
 * The abortController is provided by MapLibre for request cancellation
 */
const registerNearmapProtocol = () => {
  if (protocolRegistered) return

  addProtocol(PROTOCOL_NAME, async (params, abortController) => {
    const { url } = params

    // Remove protocol prefix to get actual URL
    const actualUrl = url.replace(`${PROTOCOL_NAME}://`, '')
    const { url: signedUrl, headers } = signRequest(actualUrl)

    // Create a Request object compatible with nearmapUsageTracker
    // Use the abortController.signal provided by MapLibre for cancellation
    const request = new Request(signedUrl, {
      signal: abortController.signal,
      headers,
    })

    try {
      // Use existing nearmapUsageTracker logic for caching and billing
      const response = await nearmapUsageTracker.fetchWithNearmapCache(request)

      // Handle case where fetchWithNearmapCache returns undefined (e.g., swallowed errors)
      if (!response || !response.ok) {
        throw new Error(
          `Failed to load Nearmap tile: ${response?.statusText || 'No response received'}`
        )
      }

      const arrayBuffer = await response.arrayBuffer()

      // MapLibre 6.11.2 expects { data: ArrayBuffer } response
      return { data: arrayBuffer }
    } catch (error) {
      // Handle abort errors gracefully
      nearmapUsageTracker.handleFetchAbortedError(error)
      throw error
    }
  })

  protocolRegistered = true
}

/**
 * Unregister the Nearmap protocol handler
 */
const unregisterNearmapProtocol = () => {
  if (!protocolRegistered) return

  removeProtocol(PROTOCOL_NAME)
  protocolRegistered = false
}

export { registerNearmapProtocol, unregisterNearmapProtocol, PROTOCOL_NAME }
